import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil, Play, Plus, Square, Trash2 } from 'lucide-react';
import type { StreamState, StreamSummary, StreamType } from '@shared/protocol';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useDocumentTitle } from '../lib/branding';
import { socket } from '../lib/socket';
import { Notice, StreamStatusBadge } from '../components/Status';
import { SeverityBadge } from '../components/SummaryPanel';

const typeLabel: Record<StreamType, string> = { hls: 'HLS', youtube: 'YouTube Live', srt: 'SRT' };

export function StreamsPage() {
  useDocumentTitle('Streams');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [streams, setStreams] = useState<StreamSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setStreams((await api<{ streams: StreamSummary[] }>('GET', '/api/streams')).streams);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load streams.');
    }
  }, []);

  useEffect(() => {
    void load();
    socket.start();
    const stop = socket.watchStatus();
    const off = socket.onMessage((msg) => {
      if (msg.type === 'status') {
        setStreams((list) => list?.map((s) => (s.id === msg.streamId ? { ...s, status: msg.state as StreamState } : s)) ?? list);
      } else if (msg.type === 'findings') {
        setStreams((list) => list?.map((s) => (s.id === msg.streamId ? { ...s, compliance: msg.summary } : s)) ?? list);
      }
    });
    return () => {
      stop();
      off();
    };
  }, [load]);

  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The action failed.');
    }
  };

  const live = streams?.filter((s) => s.status.status === 'live').length ?? 0;
  const failing = streams?.filter((s) => s.compliance?.overall === 'FAIL').length ?? 0;
  // Users see only their own streams, so the list length is what counts towards their limit.
  const limit = user?.role === 'user' ? user.streamLimit : null;
  const atLimit = limit !== null && streams !== null && streams.length >= limit;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Streams</h1>
          <p className="muted">
            Monitoring runs on the server and continues when this page is closed.
            {streams && streams.length > 0 ? ` ${live} live, ${failing} failing.` : ''}
            {limit !== null && streams ? ` You have added ${streams.length} of ${limit} ${limit === 1 ? 'stream' : 'streams'}.` : ''}
          </p>
        </div>
        {atLimit ? (
          <button type="button" className="btn btn-primary" disabled title="You have reached your stream limit. Delete a stream or ask an administrator to raise the limit.">
            <Plus size={16} aria-hidden />
            Add stream
          </button>
        ) : (
          <Link className="btn btn-primary" to="/streams/new">
            <Plus size={16} aria-hidden />
            Add stream
          </Link>
        )}
      </div>
      {error && (
        <div style={{ marginBottom: 16 }}>
          <Notice level="fail">{error}</Notice>
        </div>
      )}
      <div className="card">
        {streams === null ? (
          <div className="empty">Loading</div>
        ) : streams.length === 0 ? (
          <div className="empty">
            <p>No streams yet.</p>
            <p className="small">Add an HLS playlist, a YouTube Live link or an SRT feed to start monitoring.</p>
          </div>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Compliance</th>
                  <th className="actions">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {streams.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <Link to={`/streams/${s.id}`} className="stream-name">
                        {s.name}
                      </Link>
                      <div className="muted small mono truncate">{s.sourceLabel}</div>
                      {isAdmin && s.owner?.role === 'user' && <div className="muted small truncate">Private to {s.owner.email}</div>}
                      {s.status.message && s.status.status !== 'live' && <div className="muted small truncate">{s.status.message}</div>}
                    </td>
                    <td>{typeLabel[s.type]}</td>
                    <td>
                      <StreamStatusBadge state={s.status} />
                    </td>
                    <td>
                      {s.compliance ? (
                        <div className="compliance-cell">
                          <SeverityBadge severity={s.compliance.overall} />
                          {s.compliance.targets.map((t) => (
                            <span key={t.targetId} className="compliance-target" title={`${t.name}: ${t.status}`}>
                              {t.name} <SeverityBadge severity={t.status} />
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="muted small">{s.config.targetIds.length ? 'No data yet' : 'No presets selected'}</span>
                      )}
                    </td>
                    <td className="actions">
                      {s.monitoring ? (
                        <button type="button" className="btn btn-sm" onClick={() => void act(() => api('POST', `/api/streams/${s.id}/stop`))}>
                          <Square size={14} aria-hidden />
                          Stop
                        </button>
                      ) : (
                        <button type="button" className="btn btn-sm" onClick={() => void act(() => api('POST', `/api/streams/${s.id}/start`))}>
                          <Play size={14} aria-hidden />
                          Start
                        </button>
                      )}{' '}
                      <Link className="btn btn-sm btn-ghost btn-icon" aria-label={`Edit ${s.name}`} to={`/streams/${s.id}/edit`}>
                        <Pencil size={14} aria-hidden />
                      </Link>
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost btn-icon btn-danger"
                        aria-label={`Delete ${s.name}`}
                        onClick={() => {
                          if (confirm(`Delete "${s.name}"? Monitoring stops and its history, findings and reports are removed.`)) void act(() => api('DELETE', `/api/streams/${s.id}`));
                        }}
                      >
                        <Trash2 size={14} aria-hidden />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
